import { publicationStateForSave } from '../../utils/formPublication';
import { useOnboardingDriver } from '../onboarding/GuidedFormCreation/OnboardingDriverContext';
/*
  This example requires Tailwind CSS v2.0+ 
  
  This example requires some changes to your config:
  
  ```
  // tailwind.config.js
  module.exports = {
    // ...
    plugins: [
      // ...
      require('@tailwindcss/forms'),
    ],
  }
  ```
*/
import React, { useState, useEffect } from 'react';
import { getAuthToken } from '../../utils/auth';
import { createDoubleTheDonationClient } from '../../lib/trpc/double-the-donation-main';
import ReactTooltip from 'react-tooltip';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { uploadImage, deleteImage } from '@/api/images';
import {
  persistFormImageVersion,
  VERSION_NOT_SAVED_MESSAGE,
} from '@/api/images/persistImageVersion';
import { useLatestCallback } from '../../hooks/useLatestCallback';
import {
  duplicateEventTicketDefinitions,
  getEventTicketConfigurationErrors,
  getEventTicketMinimumSubtotal,
  normalizeEventTicketContractMetadata,
  normalizeEventTicketDefinitions,
  prepareEventTicketDefinitionsForSubmit,
  reconcileSuccessfulEventTicketMutation,
} from '../../utils/eventTicketAuthoring';

import CustomInputsTab_Events from './tabs/CustomInputsTab_Events';
import CustomFAQsTab from './tabs/CustomFAQsTab';
import EventFormConfigurationTab from './tabs/EventFormConfigurationTab';
import SalesforceFieldMapperModal from '../modals/SalesforceFieldMapperModal';
import EmailConfigurationTab from './tabs/EmailConfigurationTab';
import { FormWorkspaceTabSection } from '../../components/workspaces/forms/FormWorkspaceTabSection';
import FormEditorSectionJump from '../../components/workspaces/forms/FormEditorSectionJump';
import {
  getFormEditorConfigurationColumnClassName,
  getFormEditorLayoutClassName,
  getFormEditorPreviewColumnClassName,
  getFormEditorPreviewFrameClassName,
} from '../../components/workspaces/forms/formEditorWorkspaceLayout';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import EventFormPreview from '../previewer/EventFormPreview';
import QRCodeModal from '../modals/QRCodeModal';
import { toast } from 'sonner';
import { copyToClipboard } from '@charitystack/shared';
import OverlaySnippets from './OverlaySnippets';
import FormSharingCards from './FormSharingCards';
import ScrollFade from '../../components/ui/scroll-fade';
import { getValidationToastMessage } from '../../utils/validationToast';
import { getEventDateTimeRangeValidation } from '../../utils/eventDateTimeValidation';

import { EditorState, ContentState, convertToRaw } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';
import dayjs from 'dayjs';

// Form data interface for better type safety and organization
const initialFormData = {
  // Basic form info
  formName: '',
  formID: '',
  elementID: '',
  active: true,
  ticketContractVersion: 2,
  ticketMutationRevision: 0,

  // Event details
  eventStartDate: null,
  eventEndDate: null,
  eventStartTime: '',
  eventEndTime: '',
  eventLocation: '',
  timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  eventDetails: null, // convertedContent

  // Appearance
  color: '#9A9285', // Default warm muted color matching COLORS.FORM_DEFAULT_COLOR
  sketchPickerColor: { r: '154', g: '146', b: '133', a: '1' }, // RGB values for #9A9285
  image: null,
  resizedImage: null,
  imageWasUploaded: false, // Track if an image was ever uploaded (for delete logic)

  // Form options
  showAnonymousDonations: false,
  disableCheckoutBump: false,
  showOrganizationNameField: false,
  enablePhoneNumber: false,
  enableBillingAddress: false,
  selectedCountry: 'United States',
  enableTimeAndLocation: true,
  enableEmbedForm: true,
  enableCommunicationConsent: true,
  communicationConsentMessage: '',

  // Collections
  customInputs: [],
  customTickets: [
    {
      id: Math.random().toString(36).substring(2, 9),
      name: '',
      price: 0,
      quantity: 0,
      type: 'SINGLE',
      seats: 1,
      sold: 0,
      attendees: [],
    },
  ],
  customPromos: [],
  customFAQs: [],

  // Email configuration
  emailConfig: {
    enableCustomEmail: false,
    replyToAddress: '',
    subject: '',
    customMessage: '',
  },
};

/**
 * QR Code utility for generating scannable QR code data
 *
 * When users scan the generated QR code with their phone camera or QR scanner app:
 * 1. The QR code contains the event form URL (e.g., https://charitystack.com/attend/ABC123)
 * 2. Scanner apps will automatically detect and open this URL in the phone's browser
 * 3. Users are redirected to the live event registration form
 *
 * @param {string} url - The event form URL to encode in QR code
 * @param {number} size - QR code size in pixels (default: 200)
 * @param {string} eventTitle - Event title for metadata
 * @param {string} eventDate - Event date for metadata
 * @param {string} eventLocation - Event location for metadata
 * @returns {Object|null} QR code data object or null if URL is invalid
 */
const generateQRCodeData = (
  url,
  size = 200,
  eventTitle = '',
  eventDate = '',
  eventLocation = '',
) => {
  // Validate URL format
  if (!url || !url.startsWith('http')) {
    console.error('❌ Invalid URL for QR code generation:', url);
    return null;
  }

  return {
    url: url,
    format: 'SVG',
    size: size,
    bgColor: '#ffffff', // White background for better scanning
    fgColor: '#000000', // Black foreground for maximum contrast
    level: 'M', // Error correction level (M = ~15% damage tolerance)
    includeMargin: true,
    timestamp: new Date().toISOString(),
    generatedAt: new Date().toISOString(),
    eventTitle: eventTitle,
    eventDate: eventDate,
    eventLocation: eventLocation,
    qrType: 'event_form',
    version: '1.0',
    // Add metadata to confirm this QR redirects to the URL
    redirectsTo: url,
    scanAction: 'redirect_to_event_form',
    label: eventTitle || 'Event Registration',
    // Additional metadata for backend processing
    isActive: true,
    generatedBy: 'CreateEventDrawer',
    purpose: 'event_registration',
  };
};

// Test function to validate QR code URL
const testQRCodeRedirect = (qrCodeData) => {
  if (!qrCodeData || !qrCodeData.url) {
    return false;
  }

  // Validate URL is accessible
  try {
    new URL(qrCodeData.url);
    return true;
  } catch (error) {
    console.error('❌ Invalid URL format:', error);
    return false;
  }
};

// Generate QR code as base64 image using a QR code library (if available)
const generateQRCodeImage = async (url, size = 200) => {
  try {
    // This is a placeholder for QR code image generation
    // In a real implementation, you would use a library like qrcode or similar
    // For now, we'll return the metadata and let the backend handle image generation
    return {
      dataUrl: null, // Would contain base64 image data
      format: 'PNG',
      size: size,
      generated: true,
      url: url,
      timestamp: new Date().toISOString(),
    };
  } catch (error) {
    console.error('❌ Failed to generate QR code image:', error);
    return null;
  }
};

const HOSTED_PAGE_URL = import.meta.env.VITE_APP_MONOREPO_BASE_URL;

// Updated tabs for the new flow
const formTabs = [
  {
    id: 'form_configuration',
    name: 'Form Configuration',
    href: '#form',
    current: true,
  },
  {
    id: 'custom_fields',
    name: 'Custom Input Fields',
    href: '#customInputs',
    current: false,
  },
  { id: 'faqs', name: 'FAQs', href: '#faqs', current: false },
  { id: 'emails', name: 'Emails', href: '#emails', current: false },
];

// Form steps for stepper (moved to EventFormConfigurationTab)
const FORM_STEPS = {
  APPEARANCE: 'appearance',
  TICKETS: 'ticket',
  PROMO_CODES: 'promo',
  CHECKOUT: 'checkout',
};

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const newShade = (hexColor, magnitude) => {
  hexColor = hexColor.replace(`#`, ``);
  if (hexColor.length === 6) {
    const decimalColor = parseInt(hexColor, 16);
    let r = (decimalColor >> 16) + magnitude;
    if (r > 255) r = 255;
    if (r < 0) r = 0;
    let g = (decimalColor & 0x0000ff) + magnitude;
    if (g > 255) g = 255;
    if (g < 0) g = 0;
    let b = ((decimalColor >> 8) & 0x00ff) + magnitude;
    if (b > 255) b = 255;
    if (b < 0) b = 0;
    return `#${(g | (b << 8) | (r << 16)).toString(16)}`.toUpperCase();
  } else {
    return hexColor.toUpperCase();
  }
};

// Section Header and ButtonGroup components moved to EventFormConfigurationTab

export default function CreateEventDrawer(props) {
  const paymentsReady = useOnboardingDriver()?.chargesEnabled === true;
  // Consolidated form data state
  const [formData, setFormData] = useState(initialFormData);

  // Helper functions for updating form data
  const updateFormData = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const updateNestedFormData = (section, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [section]: { ...prev[section], [field]: value },
    }));
  };

  // Consolidated input change handler for child components
  const handleInputChange = (field, value, section = null) => {
    if (section) {
      updateNestedFormData(section, field, value);
    } else {
      updateFormData(field, value);
    }
  };

  // UI and system states (keep separate as they're not form data)
  const [merchantName, setMerchantName] = useState('');

  const [currentStep, setCurrentStep] = useState(FORM_STEPS.APPEARANCE);
  const [activePreviewTab, setActivePreviewTab] = useState('Embed Form');
  const [previewExpanded, setPreviewExpanded] = useState(false);
  const configurationSectionRef = React.useRef(null);
  const previewSectionRef = React.useRef(null);
  const [activeFormTab, setActiveFormTab] = useState({
    id: 'form_configuration',
    name: 'Form Configuration',
    current: true,
  });
  const [displayColorPicker, setDisplayColorPicker] = useState(false);
  const [invalidFields, setInvalidFields] = useState(new Set());
  const [showTabValidation, setShowTabValidation] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isActivating, setIsActivating] = useState(false);
  const [active, setActive] = useState(true);
  const [createdForm, setCreatedForm] = useState(false);
  const [persistedTicketIDs, setPersistedTicketIDs] = useState([]);
  const [showQRModal, setShowQRModal] = useState(false);
  const [activeShareCard, setActiveShareCard] = useState('link');
  const [savedQRCodeData, setSavedQRCodeData] = useState(null);

  // Salesforce Campaign ID state (ICNA Relief only)
  const [salesforceCampaignIDICNAValues, setSalesforceCampaignIDICNAValues] = useState('');
  const [salesforceFieldMapperOpen, setSalesforceFieldMapperOpen] = useState(false);

  // Editor state (complex object, keep separate)
  const [editorState, setEditorState] = useState(() => EditorState.createEmpty());
  const [convertedContent, setConvertedContent] = useState(null);

  // Handle field focus to clear validation errors
  const handleFieldFocus = (fieldName) => {
    setInvalidFields((prev) => {
      const newInvalidFields = new Set(prev);
      // Remove the exact field name
      newInvalidFields.delete(fieldName);
      // Also try to remove non-prefixed version for backward compatibility
      const nonPrefixedName = fieldName.includes('-')
        ? fieldName.split('-').slice(1).join('-')
        : fieldName;
      newInvalidFields.delete(nonPrefixedName);
      return newInvalidFields;
    });
  };

  // Function to check if a field is invalid - now supports both prefixed and non-prefixed field names
  const isFieldInvalid = (fieldName) => {
    if (!invalidFields) return false;
    if (invalidFields instanceof Set) {
      // Check for exact match first
      if (invalidFields.has(fieldName)) {
        return true;
      }
      // Check for prefixed versions
      for (const tab of formTabs) {
        if (invalidFields.has(`${tab.id}-${fieldName}`)) {
          return true;
        }
      }
      return false;
    }
    return false;
  };

  // Step validation and navigation functions moved to EventFormConfigurationTab

  const handleCopyUrl = async () => {
    const url = `${HOSTED_PAGE_URL}/attend/${formData.elementID}`;
    const success = await copyToClipboard(url);
    if (success) {
      toast.success('URL copied to clipboard');
    } else {
      toast.error('Failed to copy URL');
    }
  };

  const handleCopyEmbedCode = async () => {
    const embedCode = `<div data-entity="EVENT" data-source="CharityStack" id="${formData.elementID}"></div>`;
    const success = await copyToClipboard(embedCode);
    if (success) {
      toast.success('Embed code copied to clipboard');
    } else {
      toast.error('Failed to copy embed code');
    }
  };

  // Updated validation function that sets invalidFields and shows toast
  const validateForm = () => {
    // Always enable tab validation when validateForm is called
    if (!showTabValidation) {
      setShowTabValidation(true);
    }

    const newInvalidFields = new Set();
    const errorMessages = [];

    // Form name validation
    if (formData.formName === '') {
      newInvalidFields.add('form_configuration-appearance-formName');
      errorMessages.push('Event Name is required');
    }

    // Color validation
    if (formData.color.length !== 7) {
      newInvalidFields.add('form_configuration-appearance-color');
      errorMessages.push('Color is required');
    }

    // Event details validation when enableTimeAndLocation is true
    if (formData.enableTimeAndLocation) {
      if (!formData.eventStartDate) {
        newInvalidFields.add('form_configuration-appearance-eventStartDate');
        errorMessages.push('Event Start Date is required');
      }
      if (!formData.eventStartTime || formData.eventStartTime.trim() === '') {
        newInvalidFields.add('form_configuration-appearance-eventStartTime');
        errorMessages.push('Event Start Time is required');
      }
      const dateTimeRangeValidation = getEventDateTimeRangeValidation(formData);
      if (dateTimeRangeValidation) {
        dateTimeRangeValidation.fields.forEach((field) =>
          newInvalidFields.add(`form_configuration-appearance-${field}`),
        );
        errorMessages.push(dateTimeRangeValidation.message);
      }
    }

    // Validate description length (max 2500 characters of plain text)
    const descriptionPlainText = editorState.getCurrentContent().getPlainText('');
    if (descriptionPlainText.length > 2500) {
      newInvalidFields.add('form_configuration-appearance-description');
      errorMessages.push('Description must be 2500 characters or less');
    }

    // Custom inputs validation - only validate if user has added custom inputs
    if (formData.customInputs.length > 0) {
      for (let i = 0; i < formData.customInputs.length; i++) {
        if (formData.customInputs[i].title === '') {
          newInvalidFields.add(`custom_fields-customInput-title-${i}`);
          errorMessages.push('Custom Input Label is required');
        }
        if (formData.customInputs[i].type === 'Select an Input Type') {
          newInvalidFields.add(`custom_fields-customInput-type-${i}`);
          errorMessages.push('Custom Input Type is required');
        }
        if (
          (formData.customInputs[i].type === 'SINGLE_SELECTION_DROPDOWN' ||
            formData.customInputs[i].type === 'MULTI_SELECTION_DROPDOWN') &&
          formData.customInputs[i].options.length < 2
        ) {
          newInvalidFields.add(`custom_fields-customInput-options-${i}`);
          errorMessages.push('2 or more Custom Input Options are required for Dropdowns');
        }
      }
    }

    // Custom tickets validation
    if (formData.customTickets.length === 0) {
      errorMessages.push('At least one ticket must be made before creating an event.');
    }

    const seen_ticket_names = new Set();
    for (let i = 0; i < formData.customTickets.length; i++) {
      var ticket = formData.customTickets[i];
      if (seen_ticket_names.has(ticket.name)) {
        newInvalidFields.add(`form_configuration-ticket-name-${i}`);
        errorMessages.push('Ticket names must be unique');
      } else {
        seen_ticket_names.add(ticket.name);
      }
      if (ticket.name === '') {
        newInvalidFields.add(`form_configuration-ticket-name-${i}`);
        errorMessages.push('Ticket Name is required');
      }
      const validTicketTypes = ['SINGLE', 'GROUP'];
      if (!ticket.type || !validTicketTypes.includes(ticket.type)) {
        newInvalidFields.add(`form_configuration-ticket-type-${i}`);
        errorMessages.push('Ticket Type is required');
      }
      getEventTicketConfigurationErrors(ticket).forEach(({ field, message }) => {
        newInvalidFields.add(`form_configuration-ticket-${field}-${i}`);
        errorMessages.push(message);
      });

      if (ticket.price == null) {
        newInvalidFields.add(`form_configuration-ticket-price-${i}`);
        errorMessages.push('Ticket price is required');
      }
      if (!Number.isSafeInteger(Number(ticket.quantity)) || Number(ticket.quantity) <= 0) {
        newInvalidFields.add(`form_configuration-ticket-quantity-${i}`);
        errorMessages.push('Quantity must be a positive whole number');
      }
    }

    // Custom promos validation
    for (let i = 0; i < formData.customPromos.length; i++) {
      var promo = formData.customPromos[i];
      if (promo.codeName === '') {
        newInvalidFields.add(`form_configuration-promo-codeName-${i}`);
        errorMessages.push('Promo Code Name is required');
      }
      if (promo.codeType === 'Select an promo code Type') {
        newInvalidFields.add(`form_configuration-promo-codeType-${i}`);
        errorMessages.push('Promo Code Type is required');
      }

      if (promo.codeAmount == null || isNaN(promo.codeAmount)) {
        newInvalidFields.add(`form_configuration-promo-codeAmount-${i}`);
        errorMessages.push('Promo Code Amount is required');
      }
      if (promo.codeType === 'AMOUNT' && promo.codeAmount == 0) {
        newInvalidFields.add(`form_configuration-promo-codeAmount-${i}`);
        errorMessages.push('Promo Code Amount cannot be 0');
      }

      // Check if promo amount exceeds max allowed for AMOUNT type
      if (promo.codeType === 'AMOUNT' && promo.codeAmount > 0 && promo.tickets.length > 0) {
        const associatedTickets = promo.tickets
          .map((promoTicket) =>
            formData.customTickets.find((ticket) => ticket.id === promoTicket.id),
          )
          .filter(Boolean);

        if (associatedTickets.length > 0) {
          const minPrice = Math.min(
            ...associatedTickets.map((ticket) => getEventTicketMinimumSubtotal(ticket)),
          );
          if (minPrice > 0 && promo.codeAmount > minPrice) {
            newInvalidFields.add(`form_configuration-promo-codeAmount-${i}`);
            errorMessages.push(
              `Promo code "${promo.codeName}" discount cannot exceed $${minPrice.toFixed(2)}`,
            );
          }
        }
      }

      if (promo.tickets.length === 0) {
        newInvalidFields.add(`form_configuration-promo-tickets-${i}`);
        errorMessages.push('Tickets are required for each promo');
      }

      if (promo.codeAmount > 100 && promo.codeType === 'PERCENTAGE') {
        newInvalidFields.add(`form_configuration-promo-codeAmount-${i}`);
        errorMessages.push('Promo Code Amount cannot be more than 100%');
      }
      if (promo.codeAmount < 0 && promo.codeType === 'PERCENTAGE') {
        newInvalidFields.add(`form_configuration-promo-codeAmount-${i}`);
        errorMessages.push('Promo Code Amount cannot be less than 0');
      }
    }

    const promoErrorMessages = validatePromoCodes(formData.customPromos, formData.customTickets);
    if (promoErrorMessages.length > 0) {
      errorMessages.push(...promoErrorMessages);
    }

    // Custom FAQs validation - only validate if user has added FAQs
    if (formData.customFAQs.length > 0) {
      for (let i = 0; i < formData.customFAQs.length; i++) {
        if (formData.customFAQs[i].question === '') {
          newInvalidFields.add(`faqs-faq-question-${i}`);
          errorMessages.push('FAQ question is required');
        }
        if (formData.customFAQs[i].answer === '') {
          newInvalidFields.add(`faqs-faq-answer-${i}`);
          errorMessages.push('FAQ answer is required');
        }
      }
    }

    // Email configuration validation - only validate if custom email is enabled
    if (formData.emailConfig.enableCustomEmail === true) {
      if (formData.emailConfig.customMessage === '') {
        newInvalidFields.add('emails-email-customMessage');
        errorMessages.push('Custom message is required');
      }
      if (formData.emailConfig.subject === '') {
        newInvalidFields.add('emails-email-subject');
        errorMessages.push('Email subject is required');
      }
      if (formData.emailConfig.replyToAddress === '') {
        newInvalidFields.add('emails-email-replyToAddress');
        errorMessages.push('Reply-to address is required');
      } else {
        // Validate email format
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.emailConfig.replyToAddress)) {
          newInvalidFields.add('emails-email-replyToAddress');
          errorMessages.push('Reply-to address must be a valid email');
        }
      }
    }

    // Always update invalid fields state
    setInvalidFields(newInvalidFields);
    // Show toast with errors if any
    if (errorMessages.length > 0) {
      toast.error(getValidationToastMessage(errorMessages));
      return false;
    }

    return true;
  };

  function validatePromoCodes(CustomPromos, CustomTickets) {
    const errorMessages = [];

    CustomPromos.forEach((promo) => {
      const associatedTickets = promo.tickets
        .map((ticket2) => {
          return CustomTickets.find((ticket) => ticket.id === ticket2.id);
        })
        .filter(Boolean);

      if (associatedTickets.length === 0) {
        errorMessages.push(`Promo code: "${promo.codeName}" references invalid tickets.`);
      } else {
        const smallestTicketPrice = Math.min(
          ...associatedTickets.map((ticket) => getEventTicketMinimumSubtotal(ticket)),
        );
        const promoAmount = parseFloat(promo.codeAmount);

        if (smallestTicketPrice == 0) {
          errorMessages.push(`Free Tickets cannot be used with Promo Codes`);
        }
        if (promo.codeType === 'AMOUNT' && promoAmount > smallestTicketPrice) {
          errorMessages.push(
            `Promo value for code "${promo.codeName}" cannot be more than $${smallestTicketPrice}.`,
          );
        }
      }
    });

    return errorMessages;
  }

  const ticketPromosMap = {};
  for (const promo of formData.customPromos) {
    for (const ticket of promo.tickets) {
      if (!ticketPromosMap[ticket.id]) {
        ticketPromosMap[ticket.id] = [];
      }
      ticketPromosMap[ticket.id].push({
        codeName: promo.codeName,
        codeType: promo.codeType,
        codeAmount: `${promo.codeAmount}`,
        codeQuantity: promo.codeType === 'AMOUNT' ? `${promo.codeQuantity || 1}` : undefined,
      });
    }
  }

  const updatedTickets = formData.customTickets.map((ticket) => {
    // Check if the type of the ticket is 'Single' or 'SINGLE'
    if (ticket.type.toLowerCase() === 'single') {
      return { ...ticket, seats: 1 };
    }
    return ticket;
  });

  // Merge promos into the tickets
  const mergedTickets = updatedTickets.map((ticket) => {
    return {
      ...ticket,
      promos: ticketPromosMap[ticket.id] || [],
    };
  });
  // Tax receipt stuff
  const changeEmailSettings = async () => {
    const payload = {
      replyToEmailAddress: formData.emailConfig.replyToAddress,
      subject: formData.emailConfig.subject,
      emailMessage: formData.emailConfig.customMessage,
      requestType: 'FORMS_PAGE',
      formName: formData.formName,
      merchantName: props.merchant.merchantName,
    };
    const requestOptions = {
      method: 'POST',
      headers: {
        accept: '*/*',
        'content-type': 'application/json',
        Authorization: await getAuthToken(),
      },
      body: JSON.stringify(payload),
    };

    const deletePayload = {
      replyToEmailAddress: formData.emailConfig.replyToAddress,
      subject: formData.emailConfig.subject,
      emailMessage: formData.emailConfig.customMessage,
      requestType: 'FORMS_PAGE',
      formName: formData.formName,
      merchantName: props.merchant.merchantName,
    };
    const queryParams = Object.entries(deletePayload)
      .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
      .join('&');
    const requestOptionsDelete = {
      method: 'DELETE',
      headers: {
        accept: '*/*',
        'content-type': 'application/json',
        Authorization: await getAuthToken(),
      },
      body: JSON.stringify(deletePayload),
    };

    const url = import.meta.env.VITE_APP_EMAIL_CONFIG_URL;

    if (formData.emailConfig.enableCustomEmail === false) {
      let response = await fetch(url + '?' + queryParams, requestOptionsDelete);

      let data = await response.json();
      if (data.error) {
        setIsPublishing(false);
        toast.error(data.error);
        return;
      } else {
        // Don't call grabSelectableFunds here - it will overwrite the onFormCreated update
        // props.grabSelectableFunds();
        setIsPublishing(false);
        toast.success(createdForm ? 'Event Saved Successfully' : 'Event Created Successfully');
        setCreatedForm(true);
      }
    }

    if (formData.emailConfig.enableCustomEmail === true) {
      let response = await fetch(url, requestOptions);
      let data = await response.json();
      if (data.error) {
        setIsPublishing(false);
        toast.error(data.error);
        return;
      } else {
        // Don't call grabSelectableFunds here - it will overwrite the onFormCreated update
        // props.grabSelectableFunds();
        setIsPublishing(false);
        toast.success(createdForm ? 'Event Saved Successfully' : 'Event Created Successfully');
        setCreatedForm(true);
      }
    }
  };

  const sendToServer = async () => {
    // Validate form and show toast errors
    if (!validateForm()) {
      return;
    }

    const darkColor = newShade(formData.color, -50);

    const normalizedTickets = normalizeEventTicketDefinitions(mergedTickets);
    const v2Tickets = prepareEventTicketDefinitionsForSubmit(normalizedTickets);

    setIsPublishing(true);

    // Generate QR code data for the event
    const eventUrl = `${HOSTED_PAGE_URL}/attend/${formData.elementID || 'temp'}`;
    const qrCodeData = generateQRCodeData(
      eventUrl,
      200,
      formData.formName,
      formData.eventStartDate ? formData.eventStartDate.format('YYYY-MM-DD') : '',
      formData.eventLocation,
    );

    // Generate QR code image data
    const qrCodeImage = await generateQRCodeImage(eventUrl, 200);
    if (qrCodeData && qrCodeImage) {
      qrCodeData.imageData = qrCodeImage;
    }

    const payload = {
      formName: formData.formName,
      merchantName: props.merchant.merchantName,
      formType: 'EVENT',
      elementType: 'EVENT',
      fundraiserType: 'EVENT',
      requirePhoneNumber: formData.enablePhoneNumber,
      requireBillingAddress: formData.enableBillingAddress,
      enableCommunicationConsent: formData.enableCommunicationConsent,
      communicationConsentMessage: formData.communicationConsentMessage,
      selectedCountry: formData.selectedCountry,
      lightColor: formData.color,
      darkColor: darkColor,
      tickets: v2Tickets,
      ticketContractVersion: 2,
      ticketMutationRevision: formData.ticketMutationRevision ?? 0,
      customInputs: formData.customInputs,
      elementID: formData.elementID,
      formID: formData.formID,
      faqs: formData.customFAQs,
      eventDetails: convertedContent,
      eventStartDate: formData.enableTimeAndLocation
        ? formData.eventStartDate
          ? formData.eventStartDate.format('YYYY-MM-DD')
          : null
        : null,
      eventEndDate: formData.enableTimeAndLocation
        ? formData.eventEndDate
          ? formData.eventEndDate.format('YYYY-MM-DD')
          : null
        : null,
      eventStartTime: formData.enableTimeAndLocation ? formData.eventStartTime : '',
      eventEndTime: formData.enableTimeAndLocation ? formData.eventEndTime : '',
      eventLocation: formData.enableTimeAndLocation ? formData.eventLocation : '',
      showAnonymousDonations: formData.showAnonymousDonations,
      disableCheckoutBump: formData.disableCheckoutBump,
      showOrganizationNameField: formData.showOrganizationNameField,
      enableTimeAndLocation: formData.enableTimeAndLocation,
      enableEmbedForm: formData.enableEmbedForm,
      timeZone: formData.enableTimeAndLocation
        ? formData.timeZone
        : Intl.DateTimeFormat().resolvedOptions().timeZone,
      qrCodeData: qrCodeData,
      active: publicationStateForSave({ existingId: formData.formID, active, paymentsReady }),
      // Elements array for backend element tracking
      // For CREATE: only include QR_CODE element (backend auto-creates main form element)
      // For UPDATE: include QR_CODE with proper element ID
      elements: !createdForm
        ? [
            // QR Code element (always included on CREATE)
            {
              type: 'QR_CODE',
              qrCodeType: 'HOSTED_PAGE',
              url: '', // Will be populated by backend after form creation
            },
          ]
        : [
            // QR Code element (always included on UPDATE)
            {
              type: 'QR_CODE',
              id: formData.elementID || '',
              isSaved: true,
              savedIndicies: [], // No elements come before QR Code in event forms
              selectedValuesSoFar: ['QR Code'],
              qrCodeType: 'HOSTED_PAGE',
              url: `${HOSTED_PAGE_URL}/attend/${formData.elementID}`,
              embedSize: '',
              goal: '',
              listType: '',
              donationsOffset: '',
              raisedOffset: '',
            },
          ],
    };

    // Conditionally add salesforceCampaignID if it has a value (ICNA Relief only)
    if (salesforceCampaignIDICNAValues !== '') {
      payload.salesforceCampaignID = salesforceCampaignIDICNAValues.trim();
    }

    const requestOptions = {
      method: createdForm ? 'PATCH' : 'POST', // choose the method based on if it is an updated or a new form
      headers: {
        accept: '*/*',
        'content-type': 'application/json',
        Authorization: await getAuthToken(),
      },
      body: JSON.stringify(payload),
    };

    const url = import.meta.env.VITE_APP_FORMS_URL;

    let response = await fetch(url, requestOptions);
    let data = await response.json();

    if (data.error) {
      toast.error(data.error);
      setIsPublishing(false);
      return;
    } else {
      // Handle success
      // Handle image deletion if user removed a previously uploaded image
      // Only delete if: form was saved before, image was uploaded before, current image is cleared, no new image pending
      if (
        createdForm &&
        formData.imageWasUploaded &&
        !formData.image &&
        formData.resizedImage === null &&
        formData.elementID
      ) {
        await handleImageDelete(formData.elementID);
      }

      // Handle image upload if there's a new image
      if (formData.resizedImage) {
        if (formData.elementID != '' && formData.elementID != null) {
          await handleImageSubmit(formData.elementID);
        } else {
          await handleImageSubmit(data.elementID);
        }
      }

      const expectedRevision = createdForm ? (formData.ticketMutationRevision ?? 0) + 1 : 0;
      const { tickets: savedTickets, contract: savedContract } =
        reconcileSuccessfulEventTicketMutation({
          persistedForm: data?.form,
          normalizedTickets,
          fallbackContract: formData,
          expectedRevision,
        });
      setFormData((prev) => ({
        ...prev,
        elementID: data?.elementID ?? prev.elementID,
        formID: data?.formID ?? prev.formID,
        active: data.form?.active ?? data.active ?? payload.active,
        ...savedContract,
        customTickets: savedTickets,
      }));
      setActive(data.form?.active ?? data.active ?? payload.active);
      setPersistedTicketIDs(savedTickets.map(({ id }) => id));

      // Update QR code data with the correct URL after successful form save
      const finalEventUrl = `${HOSTED_PAGE_URL}/attend/${data.elementID || formData.elementID}`;
      const finalQrCodeData = generateQRCodeData(
        finalEventUrl,
        200,
        formData.formName,
        formData.eventStartDate ? formData.eventStartDate.format('YYYY-MM-DD') : '',
        formData.eventLocation,
      );

      if (finalQrCodeData && paymentsReady && payload.active) {
        setSavedQRCodeData(finalQrCodeData);
        // Test QR code redirect functionality
        testQRCodeRedirect(finalQrCodeData);
      }

      // Handle email settings after image upload completes
      await changeEmailSettings();

      // Call the parent callback to update the forms table AFTER email settings
      // This ensures grabSelectableFunds() doesn't overwrite our new form
      if (props.onFormCreated) {
        props.onFormCreated(data);
      }
    }

    // Re-enable UI elements if needed
    setIsPublishing(false);
  };

  const handleFormActivation = async (active) => {
    if (active && !paymentsReady) { toast.info('Enable payments before publishing this draft.'); return; }
    setIsActivating(true);
    const payload = {
      merchantName: props.merchant.merchantName,
      elementID: formData.elementID,
      formID: formData.formID,
      active: active,
      ticketContractVersion: formData.ticketContractVersion ?? 2,
      ticketMutationRevision: formData.ticketMutationRevision ?? 0,
    };

    const requestOptions = {
      method: 'PATCH', // choose the method based on if it is an updated or a new form
      headers: {
        accept: '*/*',
        'content-type': 'application/json',
        Authorization: await getAuthToken(),
      },
      body: JSON.stringify(payload),
    };

    const url = `${import.meta.env.VITE_APP_FORMS_URL}/toggle-active`;

    let response = await fetch(url, requestOptions);
    let data = await response.json();

    if (data.error) {
      toast.error(data.error);
      return;
    } else {
      const savedContract = normalizeEventTicketContractMetadata(
        {
          ticketContractVersion: data.ticketContractVersion ?? data?.form?.ticketContractVersion,
          ticketMutationRevision: data.ticketMutationRevision ?? data?.form?.ticketMutationRevision,
        },
        formData,
        { minimumRevision: (formData.ticketMutationRevision ?? 0) + 1 },
      );
      setFormData((prev) => ({
        ...prev,
        active: data.active,
        ...savedContract,
      }));
      setActive(data.active);
      if (props.onFormCreated) {
        props.onFormCreated(data);
      }
    }
    setIsActivating(false);
  };

  const sendToServerLatest = useLatestCallback(sendToServer);
  const handleFormActivationLatest = useLatestCallback(handleFormActivation);

  const handleImageDelete = async (image_id) => {
    const result = await deleteImage({
      organizationId: props.merchant?.organizationId,
      imageName: `${merchantName}_${image_id}`,
      imageType: 'event',
      nonprofitName: merchantName,
      jwtToken: await getAuthToken(),
    });

    if (result.success) {
      setFormData((prev) => ({ ...prev, image: null }));
    } else {
      toast.error(result.error || 'Failed to delete image');
    }
  };

  const handleImageSubmit = async (image_id) => {
    // Check if there's an image to upload
    if (!formData.resizedImage) {
      return;
    }

    const result = await uploadImage({
      organizationId: props.merchant?.organizationId,
      imageData: formData.resizedImage,
      imageName: `${merchantName}_${image_id}`,
      imageType: 'event',
      nonprofitName: merchantName,
      jwtToken: await getAuthToken(),
    });

    if (result.success && result.imageUrl && result.imageVersion) {
      // The upload has already replaced the stored object, so its state below is
      // set either way. A version PATCH that still fails after its bounded retry
      // is reported here rather than thrown: thrown, it either vanished into the
      // save's "non-critical" catch or aborted the rest of the save.
      try {
        await persistFormImageVersion({
          formID: image_id,
          merchantName,
          imageVersion: result.imageVersion,
          jwtToken: await getAuthToken(),
        });
      } catch (error) {
        console.error('[image version] not saved after upload:', error);
        toast.error(error?.message || VERSION_NOT_SAVED_MESSAGE);
      }
      setFormData((prev) => ({ ...prev, image: result.imageUrl, imageWasUploaded: true }));
    } else {
      toast.error(result.error || 'Failed to upload image');
    }
  };

  // Clear states when modal is closed
  const clearStates = () => {
    // close drawer
    props.setOpen(false);

    // return to default states - reset formData to initial state
    setFormData({
      ...initialFormData,
      customTickets: [
        {
          id: Math.random().toString(36).substring(2, 9),
          name: '',
          price: 0,
          quantity: 0,
          type: 'SINGLE',
          seats: 1,
          sold: 0,
          attendees: [],
        },
      ],
    });

    // Reset UI states
    setInvalidFields(new Set());
    setShowTabValidation(false);
    setIsPublishing(false);
    setIsActivating(false);
    setActiveFormTab({
      id: 'form_configuration',
      name: 'Form Configuration',
      current: true,
    });
    setCurrentStep(FORM_STEPS.APPEARANCE);
    setActivePreviewTab('Embed Form');
    setCreatedForm(false);
    setPersistedTicketIDs([]);
    setEditorState(() => EditorState.createEmpty());
    setConvertedContent(null);
    setShowQRModal(false);
    setSavedQRCodeData(null);
    setActive(true);

    // Reset salesforceCampaignID state (ICNA Relief only)
    setSalesforceCampaignIDICNAValues('');
    setSalesforceFieldMapperOpen(false);
  };

  const handlePointerDownOutside = (event) => {
    // Check if the click is within any element with a class starting with 'mbx'
    if (event.target.closest('[class^="mbx"]')) {
      event.preventDefault(); // Prevent the dialog from closing
    }
  };

  useEffect(() => {
    if (props.presentation !== 'workspace' || !props.setWorkspaceActions) return undefined;

    const actions = [];

    if (formData.formID) {
      actions.push(
        {
          key: 'save',
          label: isPublishing ? 'Saving...' : 'Save',
          variant: 'primary',
          disabled: isPublishing || isActivating,
          onClick: () => sendToServerLatest(),
        },
        {
          key: 'active',
          label: !active
            ? isActivating
              ? 'Activating...'
              : 'Publish'
            : isActivating
              ? 'Deactivating...'
              : 'Deactivate',
          disabled: isPublishing || isActivating || (!active && !paymentsReady),
          variant: !active ? 'primary' : 'danger',
          onClick: () => handleFormActivationLatest(!active),
        },
      );
    } else {
      actions.push({
        key: 'publish',
        label: isPublishing ? 'Saving...' : paymentsReady ? 'Publish' : 'Save draft',
        disabled: isPublishing || isActivating,
        variant: 'primary',
        onClick: () => sendToServerLatest(),
      });
    }

    props.setWorkspaceActions(actions);

    return () => props.setWorkspaceActions?.(null);
    // Register current button state without depending on recreated local handlers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    props.presentation,
    props.setWorkspaceActions,
    isPublishing,
    isActivating,
    formData.formID,
    active,
    paymentsReady,
    sendToServerLatest,
    handleFormActivationLatest,
  ]);

  useEffect(() => {
    const rawContentState = convertToRaw(editorState.getCurrentContent());
    let markup = draftToHtml(rawContentState);
    // if markup is empty, set it to an empty string
    if (markup.trim() === '<p></p>') {
      markup = '';
    }
    // replace all <p></p> with <br>
    markup = markup.replace(/<p><\/p>/g, '<br>');
    setConvertedContent(markup);
  }, [editorState]);

  useEffect(() => {
    setTimeout(() => {
      ReactTooltip.rebuild();
    }, 100);
    if (props.open === false) {
      clearStates();
    }
  }, [props.open]);

  // Pre-populate form data when duplicating an existing event
  useEffect(() => {
    if (!props.open || !props.duplicateFormData) return;

    const form = props.duplicateFormData;

    // Parse date strings to dayjs objects (same approach as EditEventDrawer)
    const parseDate = (dateStr) => {
      if (!dateStr) return null;
      const datePart = dateStr.split('T')[0];
      const [year, month, day] = datePart.split('-').map(Number);
      if (!year || !month || !day) return null;
      const result = dayjs(new Date(year, month - 1, day, 12, 0, 0));
      if (!result.isValid()) return null;
      return result;
    };

    // Deep-copy active tickets with new IDs and remap their embedded promos.
    // Historical archived ticket definitions intentionally stay out of the copy.
    const duplicateDefinition = duplicateEventTicketDefinitions(form.tickets, () =>
      Math.random().toString(36).substring(2, 9),
    );
    const duplicatedTickets =
      duplicateDefinition.duplicatedTickets.length > 0
        ? duplicateDefinition.duplicatedTickets
        : [
            {
              id: Math.random().toString(36).substring(2, 9),
              name: '',
              price: 0,
              quantity: 0,
              type: 'SINGLE',
              seats: 1,
              sold: 0,
              attendees: [],
            },
          ];
    const duplicatedPromos = duplicateDefinition.duplicatedPromos;

    setFormData({
      ...initialFormData,
      formName: `${form.formName || ''} Copy`,
      // Identity fields left empty - new form
      formID: '',
      elementID: '',
      active: true,
      // Event dates and time
      eventStartDate: form.enableTimeAndLocation
        ? parseDate(form.eventStartDate || form.eventDate)
        : null,
      eventEndDate: form.enableTimeAndLocation ? parseDate(form.eventEndDate) : null,
      eventStartTime: form.enableTimeAndLocation ? form.eventStartTime || '' : '',
      eventEndTime: form.enableTimeAndLocation ? form.eventEndTime || '' : '',
      eventLocation: form.enableTimeAndLocation ? form.eventLocation || '' : '',
      timeZone: form.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone,
      enableTimeAndLocation: form.enableTimeAndLocation ?? false,
      // Appearance
      color: form.lightColor || '#9A9285',
      sketchPickerColor: initialFormData.sketchPickerColor,
      // Don't copy image - it's tied to the source form's elementID
      image: null,
      resizedImage: null,
      imageWasUploaded: false,
      // Form options
      showAnonymousDonations: form.showAnonymousDonations ?? false,
      disableCheckoutBump: form.disableCheckoutBump ?? false,
      showOrganizationNameField: form.showOrganizationNameField ?? false,
      enablePhoneNumber: form.phoneNumber ?? false,
      enableBillingAddress: form.showAddress ?? false,
      selectedCountry: form.defaultCountry || 'United States',
      enableEmbedForm: form.enableEmbedForm ?? true,
      enableCommunicationConsent: form.enableCommunicationConsent ?? false,
      communicationConsentMessage: form.communicationConsentMessage || '',
      // Collections
      customInputs: form.customInputs && Array.isArray(form.customInputs) ? form.customInputs : [],
      customTickets: duplicatedTickets,
      customPromos: duplicatedPromos,
      customFAQs: form.faqs && Array.isArray(form.faqs) ? form.faqs : [],
      // Email config (reset - will be fetched separately if needed)
      emailConfig: {
        enableCustomEmail: false,
        replyToAddress: '',
        subject: '',
        customMessage: '',
      },
    });

    // Set merchant name
    if (form.nonprofitName) {
      setMerchantName(form.nonprofitName);
    }

    // Populate rich text editor if eventDetails exist
    if (form.eventDetails) {
      try {
        const contentBlock = htmlToDraft(form.eventDetails);
        if (contentBlock) {
          const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
          setEditorState(EditorState.createWithContent(contentState));
          setConvertedContent(form.eventDetails);
        }
      } catch {
        setConvertedContent(form.eventDetails);
      }
    }

    // Fetch email config from the source form so it can be pre-populated for the duplicate
    const loadSourceEmailConfig = async () => {
      if (!form.formName) return;
      try {
        const requestOptions = {
          method: 'GET',
          headers: {
            accept: '*/*',
            'content-type': 'application/json',
            Authorization: await getAuthToken(),
          },
        };
        const params = new URLSearchParams();
        params.append('requestType', 'FORMS_PAGE');
        params.append('formName', form.formName);
        const url = import.meta.env.VITE_APP_EMAIL_CONFIG_URL;
        const response = await fetch(url + `?${params.toString()}`, requestOptions);
        if (!response.ok) return;
        const emailData = await response.json();
        if (emailData && emailData.message !== 'Unauthorized') {
          setFormData((prev) => ({
            ...prev,
            emailConfig: {
              enableCustomEmail: true,
              replyToAddress: emailData.replyToEmail || '',
              subject: emailData.emailSubject || '',
              customMessage: emailData.emailMessage || '',
            },
          }));
        }
      } catch (error) {
        console.error('Failed to load source form email configuration:', error);
      }
    };
    loadSourceEmailConfig();
  }, [props.open, props.duplicateFormData]);

  // Update promo codes when tickets change (like original CustomPromoTab)
  useEffect(() => {
    if (formData.customPromos.length > 0) {
      let newPromos = formData.customPromos.map((promo) => ({
        ...promo,
        tickets: promo.tickets.map((ticket) => {
          let newTicket = formData.customTickets.find((t) => t.id === ticket.id);
          return {
            id: ticket.id,
            name: newTicket ? newTicket.name : ticket.name,
          };
        }),
        tickets2: [
          ...promo.tickets.map((ticket) => {
            let newTicket = formData.customTickets.find((t) => t.id === ticket.id);
            return {
              id: ticket.id,
              name: newTicket ? newTicket.name : ticket.name,
            };
          }),
        ],
      }));
      setFormData((prev) => ({ ...prev, customPromos: newPromos }));
    }
  }, [formData.customTickets]);

  useEffect(() => {
    const myFunction = async () => {
      const organizationId = props.merchant?.organizationId;
      const nonprofit = props.merchant?.merchantName;
      let auth = await getAuthToken();
      if (props.merchant !== null) {
        // grab double the donation api key if it exists
        setMerchantName(props.merchant['merchantName']);
        await createDoubleTheDonationClient({ organizationId, authorization: auth }).getApiKeys({
          nonprofit,
        });
      }
    };
    myFunction();
  }, [props.merchant]);

  // Pre-fill the form color from the org's brand color when the drawer opens.
  // Only applies when: (a) drawer is open, (b) org has a brand color set, and
  // (c) the color field is still at the system default — meaning the user hasn't
  // picked a different color during this session.
  // clearStates() resets color to '#9A9285' on close, so re-opening the drawer
  // correctly re-applies the org's brand color.
  useEffect(() => {
    if (!props.open) return;
    const orgBrandColor = props.merchant?.brandColor;
    if (!orgBrandColor) return;
    setFormData((prev) => {
      if (prev.color !== '#9A9285') return prev; // user already changed it
      return { ...prev, color: orgBrandColor };
    });
  }, [props.open, props.merchant]);

  const renderFormTabContent = () => {
    switch (activeFormTab.name) {
      case 'Form Configuration': {
        return (
          <EventFormConfigurationTab
            workspacePresentation={props.presentation === 'workspace'}
            formData={formData}
            handleInputChange={handleInputChange}
            editorState={editorState}
            setEditorState={setEditorState}
            merchantName={merchantName}
            currentStep={currentStep}
            setCurrentStep={setCurrentStep}
            displayColorPicker={displayColorPicker}
            setDisplayColorPicker={setDisplayColorPicker}
            handleFieldFocus={handleFieldFocus}
            isFieldInvalid={isFieldInvalid}
            createdForm={createdForm}
            setInvalidFields={setInvalidFields}
            invalidFields={invalidFields}
            salesforceCampaignIDICNAValues={salesforceCampaignIDICNAValues}
            setSalesforceCampaignIDICNAValues={setSalesforceCampaignIDICNAValues}
            setSalesforceFieldMapperOpen={setSalesforceFieldMapperOpen}
            persistedTicketIDs={persistedTicketIDs}
          />
        );
      }

      case 'Custom Input Fields':
        return (
          <FormWorkspaceTabSection
            tabName="Custom Input Fields"
            enabled={props.presentation === 'workspace'}
          >
            <CustomInputsTab_Events
              showSectionHeader={props.presentation !== 'workspace'}
              customInputs={formData.customInputs}
              setCustomInputs={(value) => handleInputChange('customInputs', value)}
              handleFieldFocus={handleFieldFocus}
              isFieldInvalid={isFieldInvalid}
              setInvalidFields={setInvalidFields}
            />
          </FormWorkspaceTabSection>
        );

      case 'FAQs':
        return (
          <FormWorkspaceTabSection tabName="FAQs" enabled={props.presentation === 'workspace'}>
            <CustomFAQsTab
              showSectionHeader={props.presentation !== 'workspace'}
              customFAQs={formData.customFAQs}
              setCustomFAQs={(value) => handleInputChange('customFAQs', value)}
              handleFieldFocus={handleFieldFocus}
              isFieldInvalid={isFieldInvalid}
              setInvalidFields={setInvalidFields}
            />
          </FormWorkspaceTabSection>
        );

      case 'Emails':
        return (
          <FormWorkspaceTabSection tabName="Emails" enabled={props.presentation === 'workspace'}>
            <EmailConfigurationTab
              showSectionHeader={props.presentation !== 'workspace'}
              emailConfig={formData.emailConfig}
              setEmailConfig={(newEmailConfig) => handleInputChange('emailConfig', newEmailConfig)}
              handleFieldFocus={handleFieldFocus}
              isFieldInvalid={isFieldInvalid}
              setInvalidFields={setInvalidFields}
            />
          </FormWorkspaceTabSection>
        );

      default:
        return null;
    }
  };

  return (
    <Sheet
      embedded={props.presentation === 'workspace'}
      open={props.open}
      onOpenChange={(open) => {
        if (!open) {
          clearStates();
        }
        props.setOpen(open);
      }}
    >
      <SheetContent
        embedded={props.presentation === 'workspace'}
        side="bottom"
        onPointerDownOutside={handlePointerDownOutside}
        className={
          props.presentation === 'workspace'
            ? 'h-auto overflow-visible rounded-none bg-transparent p-0'
            : 'pointer-events-none h-[95vh] overflow-hidden rounded-t-[10px] bg-white'
        }
      >
        <div
          className={`pointer-events-none mx-auto flex w-full max-w-[1600px] flex-col ${
            props.presentation === 'workspace' ? 'h-auto px-3' : 'h-full'
          }`}
        >
          {props.presentation !== 'workspace' && (
            <header className="pointer-events-auto shrink-0 border-b border-paper-inset bg-white pb-3">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="flex items-start space-x-2">
                  <div className="flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-[19px] border border-edge bg-paper-inset">
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M5.625 3.75C4.17525 3.75 3 4.92525 3 6.375C3 7.82475 4.17525 9 5.625 9H18.375C19.8247 9 21 7.82475 21 6.375C21 4.92525 19.8247 3.75 18.375 3.75H5.625Z"
                        fill="#6B6458"
                      />
                      <path
                        d="M3.75 11.25C3.33579 11.25 3 11.5858 3 12C3 12.4142 3.33579 12.75 3.75 12.75H20.25C20.6642 12.75 21 12.4142 21 12C21 11.5858 20.6642 11.25 20.25 11.25H3.75Z"
                        fill="#6B6458"
                      />
                      <path
                        d="M3 15.75C3 15.3358 3.33579 15 3.75 15H20.25C20.6642 15 21 15.3358 21 15.75C21 16.1642 20.6642 16.5 20.25 16.5H3.75C3.33579 16.5 3 16.1642 3 15.75Z"
                        fill="#6B6458"
                      />
                      <path
                        d="M3.75 18.75C3.33579 18.75 3 19.0858 3 19.5C3 19.9142 3.33579 20.25 3.75 20.25H20.25C20.6642 20.25 21 19.9142 21 19.5C21 19.0858 20.6642 18.75 20.25 18.75H3.75Z"
                        fill="#6B6458"
                      />
                    </svg>
                  </div>
                  <div>
                    <p className="text-base font-bold text-ink-muted">Create Event</p>
                    <p className="text-sm text-ink-muted">
                      Customize the form by inputting additional details below
                    </p>
                  </div>
                </div>
                <div className="flex flex-row flex-wrap items-center gap-2 sm:justify-end">
                  <Button
                    variant="ghost"
                    onClick={() => {
                      props.setOpen(false);
                      clearStates();
                    }}
                  >
                    Close
                  </Button>

                  {formData.formID && (
                    <Button
                      onClick={() => sendToServer()}
                      disabled={isPublishing || isActivating}
                      variant="primary"
                      size="workspace"
                    >
                      {isPublishing ? 'Saving...' : 'Save'}
                    </Button>
                  )}

                  {/* Activate/Deactivate Button */}
                  {formData.formID && (
                    <Button
                      onClick={() => handleFormActivation(!active)}
                      disabled={isActivating || isPublishing || (!active && !paymentsReady)}
                      variant={!active ? 'primary' : 'danger'}
                      size="workspace"
                    >
                      {!active
                        ? isActivating
                          ? 'Activating...'
                          : 'Publish'
                        : isActivating
                          ? 'Deactivating...'
                          : 'Deactivate'}
                    </Button>
                  )}

                  {/* Publish Button - Now handles save and publish */}
                  {!formData.formID && (
                    <Button
                      onClick={() => sendToServer()}
                      variant="primary"
                      size="workspace"
                      disabled={isPublishing || isActivating}
                    >
                      {isPublishing ? 'Saving...' : paymentsReady ? 'Publish' : 'Save draft'}
                    </Button>
                  )}
                </div>
              </div>
            </header>
          )}

          {/* <div className="pb-3 pl-[2.3rem] pr-[3.7rem]">
          </div> */}

          {/* Main Content - Split Layout */}
          {props.presentation === 'workspace' && (
            <FormEditorSectionJump
              configurationRef={configurationSectionRef}
              previewRef={previewSectionRef}
            />
          )}

          <ScrollFade
            containerClassName={
              props.presentation === 'workspace' ? 'overflow-visible' : 'min-h-0 flex-1'
            }
            className={getFormEditorLayoutClassName({
              workspace: props.presentation === 'workspace',
              expanded: previewExpanded,
              interactionClassName: 'pointer-events-auto',
            })}
            fadeClassName="rounded-b-md"
            showTopFade={props.presentation !== 'workspace'}
            topFadeClassName="rounded-t-md"
            showScrollIndicator={props.presentation !== 'workspace'}
            scrollIndicatorOffset={props.presentation === 'workspace' ? '-10px' : '0px'}
          >
            {/* Left Side - Preview Section */}
            <div
              ref={previewSectionRef}
              role={props.presentation === 'workspace' ? 'region' : undefined}
              tabIndex={props.presentation === 'workspace' ? -1 : undefined}
              aria-label={props.presentation === 'workspace' ? 'Form preview' : undefined}
              className={getFormEditorPreviewColumnClassName({
                workspace: props.presentation === 'workspace',
                allowOverflow: props.presentation !== 'workspace',
                minHeightClassName: formData.elementID
                  ? activePreviewTab === 'Embed Form'
                    ? 'min-h-[600px]'
                    : 'min-h-[800px]'
                  : activePreviewTab === 'Embed Form'
                    ? 'min-h-[400px]'
                    : 'min-h-[600px]',
              })}
            >
              {/* Preview */}
              <div
                className={getFormEditorPreviewFrameClassName({
                  workspace: props.presentation === 'workspace',
                  sticky: !formData.elementID,
                })}
              >
                <EventFormPreview
                  formName={formData.formName}
                  image={formData.image}
                  color={formData.color}
                  convertedContent={convertedContent}
                  customTickets={formData.customTickets}
                  ticketContractVersion={formData.ticketContractVersion}
                  customFAQs={formData.customFAQs}
                  activePreviewTab={activePreviewTab}
                  setActivePreviewTab={setActivePreviewTab}
                  merchantName={merchantName}
                  elementID={formData.elementID || 'NEW_FORM'}
                  eventStartDate={
                    formData.eventStartDate ? formData.eventStartDate.format('YYYY-MM-DD') : ''
                  }
                  eventEndDate={
                    formData.eventEndDate ? formData.eventEndDate.format('YYYY-MM-DD') : ''
                  }
                  eventStartTime={formData.eventStartTime}
                  eventEndTime={formData.eventEndTime}
                  eventLocation={formData.eventLocation}
                  timeZone={formData.timeZone}
                  enableTimeAndLocation={formData.enableTimeAndLocation}
                  enableEmbedForm={formData.enableEmbedForm}
                  customInputs={formData.customInputs}
                  showAnonymousDonations={formData.showAnonymousDonations}
                  showOrganizationNameField={formData.showOrganizationNameField}
                  active={active}
                  formType="EVENT"
                  studioMode={props.presentation === 'workspace'}
                  isExpanded={previewExpanded}
                  onToggleExpanded={
                    props.presentation === 'workspace'
                      ? () => setPreviewExpanded((expanded) => !expanded)
                      : undefined
                  }
                />
              </div>

              {/* URL and Embed Sections */}
              {createdForm && formData.elementID && paymentsReady && active === true && (
                <div className="rounded-lg bg-white">
                  <FormSharingCards
                    elementID={formData.elementID}
                    formType="attend"
                    embedEntity="EVENT"
                    hostedPageUrl={`${HOSTED_PAGE_URL}/attend/${formData.elementID}`}
                    websiteUrl={props.merchant?.organizationBaseURLs?.[0]}
                    color={formData.color}
                    onShowQR={() => setShowQRModal(true)}
                    hasQRCode={!!savedQRCodeData}
                  />
                </div>
              )}
            </div>

            {/* Right Side - Form Configuration */}
            <div
              ref={configurationSectionRef}
              role={props.presentation === 'workspace' ? 'region' : undefined}
              tabIndex={props.presentation === 'workspace' ? -1 : undefined}
              aria-label={props.presentation === 'workspace' ? 'Form editor' : undefined}
              className={getFormEditorConfigurationColumnClassName({
                workspace: props.presentation === 'workspace',
              })}
            >
              {/* Form Tabs */}
              <div className=" border-edge bg-white">
                <nav
                  className="-mb-px flex space-x-6 overflow-x-auto border-b pb-2 md:overflow-x-hidden md:pb-0 pt-2"
                  aria-label="Tabs"
                >
                  {formTabs.map((tab) => {
                    // Check if this tab has errors by looking for fields with the tab's ID prefix
                    const tabHasErrors = Array.from(invalidFields).some((field) =>
                      field.startsWith(tab.id + '-'),
                    );
                    return (
                      <a
                        key={tab.id}
                        href={tab.href}
                        onClick={(e) => {
                          e.preventDefault();
                          setActiveFormTab({
                            name: tab.name,
                            current: true,
                          });
                          // Optionally clear tab validation when user switches tabs to fix issues
                          // setShowTabValidation(false);
                        }}
                        className={classNames(
                          tab.name === activeFormTab.name
                            ? tabHasErrors
                              ? 'border-red-500 text-red-600'
                              : 'border-ink-secondary text-ink-secondary'
                            : tabHasErrors
                              ? 'border-transparent text-red-500 hover:text-red-600'
                              : 'border-transparent text-ink-muted hover:text-ink-secondary',
                          'whitespace-nowrap px-1 pb-1.5 text-[13px] font-medium border-b-2 border-x-0 border-t-0 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0',
                        )}
                      >
                        {tab.name}
                      </a>
                    );
                  })}
                </nav>
              </div>

              {/* Form Content */}
              <div className="flex-1 lg:overflow-visible py-4">{renderFormTabContent()}</div>
            </div>
          </ScrollFade>
        </div>
      </SheetContent>

      {/* QR Code Modal */}
      <QRCodeModal
        open={showQRModal && paymentsReady && active === true}
        setOpen={setShowQRModal}
        url={savedQRCodeData?.url || `${HOSTED_PAGE_URL}/attend/${formData.elementID}`}
        title="Event QR Code"
        label={'Scan to Attend'}
        instructionText="Download the QR Code below to redirect people to this Event."
        bannerColor={formData.color}
      />

      {/* Salesforce Field Mapper Modal */}
      <SalesforceFieldMapperModal
        open={salesforceFieldMapperOpen}
        setOpen={setSalesforceFieldMapperOpen}
        formName={formData.formName || 'Event'}
        campaignId={salesforceCampaignIDICNAValues}
        setCampaignId={setSalesforceCampaignIDICNAValues}
      />
    </Sheet>
  );
}
